/**
 * Sprint 1070.C3 — Shared <StudioShell> wrapper.
 *
 * One canonical chrome (sidebar + top toolbar + mission breadcrumb +
 * content area) that every studio in the family (Web / Creative / Finance
 * / Operate / Sales / Mailing) can wrap around its content.
 *
 * Why: before this sprint, every studio hand-rolled its sidebar + toolbar
 * + breadcrumb. Subtle drift was inevitable. Also when a sidebar deeplink
 * navigated AWAY from the studio (e.g. Web Studio → Marketing/Website), the
 * mission breadcrumb disappeared — sidebar context was lost.
 *
 * After: any page can wrap itself in `<StudioShell>` with its studio
 * identity + sidebar registry + mission stages, and inherit consistent
 * chrome. Sidebar deeplinks that route to canonical pages (like
 * `/marketing/website`) can ALSO wrap themselves in `<StudioShell>` when
 * `?source=web-studio` is set — preserving the breadcrumb everywhere.
 *
 * Props
 *   studio              — { id, label, tagline, icon, accent (hex) }
 *   sidebarGroups       — Array<{ key, label, icon, items: [{ key, label, icon, href, testId? }] }>
 *   activeSection       — slug of the active section (used for sidebar expansion + breadcrumb stage selection)
 *   sectionToStage      — { [sectionKey]: stageSlug } map for breadcrumb highlighting
 *   missionStages       — Array<{ slug, label, count, href }> — passed to MissionBreadcrumb
 *   onSectionPick       — (sectionKey, item) => void; called when user clicks a sidebar item
 *   topbarTitle         — string ("Web Studio — Create") — defaults to `{studio.label} — {activeGroupLabel}`
 *   topbarActions       — optional ReactNode rendered on the right of the toolbar
 *   children            — main content
 */
import { useMemo } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import MissionBreadcrumb from "./MissionBreadcrumb";
import CreditsPill from "./CreditsPill";
import SidebarGroup from "../web-studio/SidebarGroup";

export default function StudioShell({
  studio,
  sidebarGroups,
  activeSection,
  activeItemKey,  // Sprint 1070.C5-prep3 — key of the active sub-item, used to
                  // highlight the sidebar nav item the user is currently in.
                  // Falls back to the group key when not provided.
  sectionToStage,
  activeStageSlug,        // Sprint 1070.C5 — explicit override for the highlighted
                          // mission stage. Wins over `sectionToStage[activeSection]`.
                          // Useful when a single section has many tabs that map to
                          // different stages (e.g. Creative Studio's social sub-tabs).
  missionStages,
  missionLabelOverride,   // Sprint 1070.C5 — explicit label for the child mission
                          // breadcrumb. Wins over `studio.missionLabel`. Useful when
                          // the label is localized server-side (e.g. Creative Studio
                          // returns `mission_label` from `/mission-breadcrumb`).
  breadcrumbRenderDrawer, // Sprint 1077 — optional renderDrawer pass-through to
                          // MissionBreadcrumb. Lets each studio mount its own
                          // per-section stale-rows drawer behind the count badge
                          // (Industry already had this; Creative gets it via this prop).
  onSectionPick,
  topbarTitle,
  topbarActions,
  showCredits,  // Sprint 1070.C5 — default true; pass `false` to hide the
                // shared credits pill (e.g. a studio that doesn't consume AI credits).
  children,
}) {
  const { t } = useLanguage();

  // Find the active group entry by section key so the toolbar title can
  // mirror the legacy "{studio} — {group label}" pattern by default.
  const activeGroup = useMemo(() => {
    return (sidebarGroups || []).find((g) => g.key === activeSection) || (sidebarGroups || [])[0] || null;
  }, [sidebarGroups, activeSection]);

  // Sprint 1070.C4 — stages render in natural order; MissionBreadcrumb
  // highlights the current stage via `currentSlug` (matches Creative
  // Studio's reference design where stage 5 of 8 can be the active pill).
  // Sprint 1070.C5 — `activeStageSlug` prop wins over the `sectionToStage`
  // map lookup; falls back to the first stage when nothing else resolves.
  const currentStageSlug = activeStageSlug
    || sectionToStage?.[activeSection]
    || (missionStages?.[0]?.slug ?? null);

  const StudioIcon = studio?.icon;

  return (
    <div
      className="min-h-screen flex bg-[var(--app-bg)]"
      data-testid={`studio-shell-${studio?.id || "unknown"}`}
    >
      {/* Left rail */}
      <aside
        className="w-60 border-r border-[var(--app-border)] bg-[var(--app-bg-secondary)]/40 flex-shrink-0 py-4 overflow-y-auto sticky top-0 self-start"
        style={{ height: "100vh" }}
        data-testid={`studio-shell-sidebar-${studio?.id}`}
      >
        <div className="px-3 mb-3">
          <div className="flex items-center gap-2">
            {StudioIcon ? (
              <StudioIcon className="w-4 h-4" style={{ color: studio.accent || "#3B82F6" }} />
            ) : null}
            <h2 className="text-sm font-bold text-[var(--app-text)]">{t(studio?.label || "")}</h2>
          </div>
          {studio?.tagline && (
            <p className="text-[10px] text-[var(--app-text-muted)] mt-0.5">{t(studio.tagline)}</p>
          )}
        </div>

        {(sidebarGroups || []).map((group) => (
          <SidebarGroup
            key={group.key}
            id={group.key}
            label={group.label}
            Icon={group.icon}
            items={group.items}
            defaultOpen={group.key === activeSection}
            active={activeItemKey || null}
            activeGroup={activeSection}
            onPick={(item) => onSectionPick && onSectionPick(group.key, item)}
          />
        ))}
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Sprint 1070.C5 — the redundant "Studio > Section" toolbar was
            removed per paco's reference. The parent `MISSION: GROW THE
            BRAND` breadcrumb is rendered by the global `Layout` shell —
            do NOT re-render it here. The studio header row below has:
              LEFT  → child mission breadcrumb (e.g. "BUILD. SHIP. OPTIMIZE.")
              RIGHT → credits pill + optional `topbarActions` (e.g. Switch mode)
            …on a SINGLE horizontal line, then content directly under. */}
        <main className="flex-1 p-6" data-testid={`studio-shell-main-${studio?.id}`}>
          <div
            className="flex items-start justify-between gap-4 mb-5"
            data-testid={`studio-shell-header-row-${studio?.id}`}
          >
            <div className="flex-1 min-w-0">
              {missionStages && missionStages.length > 0 && (
                <div data-testid={`studio-shell-breadcrumb-wrap-${studio?.id}`}>
                  <MissionBreadcrumb
                    missionLabel={missionLabelOverride || t(studio?.missionLabel || "")}
                    sections={missionStages}
                    currentSlug={currentStageSlug}
                    accent={studio?.accent || "#3B82F6"}
                    studioId={studio?.id || "studio"}
                    t={t}
                    renderDrawer={breadcrumbRenderDrawer}
                  />
                </div>
              )}
            </div>
            <div
              className="flex items-center gap-2 flex-shrink-0 pt-1"
              data-testid={`studio-shell-actions-${studio?.id}`}
            >
              {showCredits !== false && <CreditsPill />}
              {topbarActions}
            </div>
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}
